import { type CatalogueChange, MAX_CATALOGUE_UPDATE } from "./catalogue";
import { parseCatalogueCost, purchaseCostInput, validateAcquiredDate } from "./inventoryFacts";
import type { InventoryItem } from "./types";

const headers = ["item_id", "sku", "item_title", "currency", "purchase_cost", "stock_location", "acquired_date"];

/** RFC-style quoted CSV, or the tab-separated text copied by spreadsheet apps. */
export function parseCatalogueRows(input: string): string[][] {
  if (input.length > 500_000) throw new Error("Use a CSV or pasted selection under 500 KB.");
  const text = input.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  const delimiter = text.split("\n", 1)[0].includes("\t") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [], cell = "", quoted = false, closed = false;
  const endCell = () => { row.push(cell); cell = ""; closed = false; };
  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') { cell += '"'; index++; }
      else if (char === '"') { quoted = false; closed = true; }
      else cell += char;
    } else if (char === delimiter) endCell();
    else if (char === "\n" || char === "\r") { endCell(); rows.push(row); row = []; }
    else if (char === '"' && cell === "" && !closed) quoted = true;
    else if (char === '"' || closed) throw new Error("Check the quotation marks in your spreadsheet data.");
    else cell += char;
  }
  if (quoted) throw new Error("A quoted cell is unfinished. Paste or upload the complete rows.");
  endCell(); rows.push(row);
  return rows.filter((candidate) => candidate.some((value) => value.trim()));
}

export function catalogueChangesFromSpreadsheet(text: string, items: InventoryItem[], now = new Date()): CatalogueChange[] {
  const rows = parseCatalogueRows(text);
  const columns = rows.shift()?.map((header) => header.trim().toLowerCase());
  if (!columns || columns.some((header) => !headers.includes(header)) || new Set(columns).size !== columns.length || (!columns.includes("item_id") && !columns.includes("sku"))) throw new Error("Include an item_id or sku column. Supported columns: item_id, sku, item_title, currency, purchase_cost, stock_location, acquired_date.");
  if (!rows.length || rows.length > MAX_CATALOGUE_UPDATE) throw new Error(`Include between 1 and ${MAX_CATALOGUE_UPDATE} item rows.`);
  const used = new Set<string>();
  return rows.map((row, index) => {
    const label = `Row ${index + 2}`;
    if (row.length !== columns.length) throw new Error(`${label}: the number of cells does not match the header.`);
    const cell = (name: string) => columns.includes(name) ? row[columns.indexOf(name)].trim() : "";
    const id = cell("item_id"), sku = cell("sku");
    let matches = items.filter((item) => id ? item.id === id : sku && item.sku.toLowerCase() === sku.toLowerCase());
    if (!matches.length && /^'[=+\-@]/.test(id || sku)) matches = items.filter((item) => id ? item.id === id.slice(1) : item.sku.toLowerCase() === sku.slice(1).toLowerCase());
    if (matches.length !== 1) throw new Error(`${label}: ${matches.length ? "SKU matches more than one item; use item_id" : "no matching inventory item"}.`);
    const item = matches[0];
    if (cell("currency") && cell("currency").toUpperCase() !== item.purchaseCost.currency.toUpperCase()) throw new Error(`${label}: use this item’s ${item.purchaseCost.currency} purchase-cost currency.`);
    if (id && sku && item.sku.toLowerCase() !== sku.toLowerCase() && "'" + item.sku.toLowerCase() !== sku.toLowerCase()) throw new Error(`${label}: item_id and SKU refer to different items.`);
    if (["sold", "archived"].includes(item.status)) throw new Error(`${label}: this item is sold or archived.`);
    if (used.has(item.id)) throw new Error(`${label}: this item appears more than once.`);
    used.add(item.id);
    const update: CatalogueChange["update"] = {};
    try {
      const cost = cell("purchase_cost");
      if (cost) Object.assign(update, cost.toLowerCase() === "unknown" ? { purchaseCostMinor: 0, purchaseCostStatus: "unknown" } : { purchaseCostMinor: parseCatalogueCost(cost), purchaseCostStatus: "known" });
      const location = cell("stock_location");
      if (location) {
        if (location.length > 120) throw new Error("Stock location must be at most 120 characters.");
        update.stockLocation = location.toLowerCase() === "unknown" ? null : item.stockLocation && location === "'" + item.stockLocation ? item.stockLocation : location;
      }
      const date = cell("acquired_date");
      if (date) { validateAcquiredDate(date.toLowerCase() === "unknown" ? null : date, now); update.acquiredAt = date.toLowerCase() === "unknown" ? null : date; }
    } catch (error) { throw new Error(`${label}: ${error instanceof Error ? error.message : "check the values."}`); }
    if (!Object.keys(update).length) throw new Error(`${label}: fill in at least one detail; blank cells leave values unchanged.`);
    return { itemID: item.id, expectedUpdatedAt: item.updatedAt, update };
  });
}

export function catalogueTemplateCSV(items: InventoryItem[]): string {
  // Prefix spreadsheet formulas with an apostrophe. Import matching recognises this export escape.
  const quote = (value: string) => `"${(/^[=+\-@\t\r]/.test(value) ? "'" + value : value).replace(/"/g, '""')}"`;
  return [headers.join(","), ...items.map((item) => [item.id, item.sku, item.title, item.purchaseCost.currency, purchaseCostInput(item), item.stockLocation ?? "", item.acquiredAt?.slice(0, 10) ?? ""].map(quote).join(","))].join("\r\n");
}
